import { Permission, userHasAnyPermission } from "~/domains/user/utils/permissions";
import { getSessionUserSnapshot } from "~/domains/user/sessionRuntime";

const DISTRIBUTOR_ROLE = "ROLE_B2BPURCHASING-DISTRIBUTOR";
const PURCHASER_ROLE = "ROLE_B2BPURCHASING";
const ROLE_B2BINQUIRER = "ROLE_B2BINQUIRER";
const ROLE_B2BINTERNALCUSTOMERGROUP = "ROLE_B2BPURCHASING-INTERNALCUSTOMERGROUP";
const REGISTERED_ROLE = "ROLE_REGISTEREDUSERGROUP";
const B2B_ROLE = "ROLE_B2BGROUP";
const ROLE_B2BCONSUMER = "ROLE_B2BCONSUMER";
const ROLE_B2BCONSUMERDISTRIBUTOR = "ROLE_B2BCONSUMER-DISTRIBUTOR";
const SALESMAN_ROLE = "ROLE_ADDTOQUOTEACTION";
export const NEWSLETTER_ROLE = "ROLE_NEWSLETTERGROUP";

const getAllRoles = () => getSessionUserSnapshot().roles;

// Indexof instead of === because our roles can have shop specific suffixes.
const has = (requiredRole: string) => (role: string) => role.indexOf(requiredRole) !== -1;

export const userRolesAreAuthenticated = (roles: readonly string[]) => {
  return !roles.includes("ROLE_ANONYMOUS") && !roles.includes("ROLE_PUBLIC");
};

export const userRolesAreRegistered = (roles: readonly string[]) => {
  return roles.some(has(REGISTERED_ROLE));
};

/**
 * Possible roles:
 *
 * Anonymous A.K.A Public user - everyone who is not logged in
 * Registered user - Someone who registered via the registration form
 *
 * Customer - Everyone who had their account created by Schaeffler, this includes:
 *    OEM - Basic user who cannot buy anything
 *    Purchaser - Basic user who can buy items
 *    Distributor - Purchaser who is associated with multiple B2B accounts and can buy using any of them
 *    Salesman - Also associated with multiple B2B accounts. Schaeffler employee, sets up quotes instead of carts
 *
 */

export const userIsPurchaser = () => {
  return getAllRoles().filter(has(PURCHASER_ROLE)).length > 0;
};

export const userRolesAreSalesman = (roles: readonly string[]) => roles.includes(SALESMAN_ROLE);

export const userIsSalesman = () => userRolesAreSalesman(getAllRoles());

export const userIsDistributor = () => {
  return getAllRoles().filter(has(DISTRIBUTOR_ROLE)).length > 0;
};

export const userIsOEM = () => {
  return userIsPurchaser() && !userIsDistributor();
};

/**
 *
 */
export const userIsLoggedIn = () => {
  return !userHasAnyPermission(Permission.ANONYMOUS, Permission.GO_PUBLIC);
};

/**
 * "General"
 */
export const userIsPublic = () => !userIsLoggedIn();

/**
 * "Plus"
 */
export const userIsRegistered = () => {
  return userRolesAreRegistered(getAllRoles());
};

/**
 * "Business"
 */
export const userIsCustomer = () => {
  return userIsLoggedIn() && !userIsRegistered();
};

export const userIsInquirer = () => {
  return getAllRoles().filter(has(ROLE_B2BINQUIRER)).length > 0;
};

export const userIsB2BAccount = () => {
  return getAllRoles().filter(has(B2B_ROLE)).length > 0;
};

export const userIsInternalCustomer = () => {
  return getAllRoles().filter(has(ROLE_B2BINTERNALCUSTOMERGROUP)).length > 0;
};

export const userHasMarkettingPermission = () => {
  return getAllRoles().some(has(NEWSLETTER_ROLE));
};

export const userIsConsumer = () => {
  return getAllRoles().some(has(ROLE_B2BCONSUMER));
};

export const userIsConsumerDistributor = () => {
  return getAllRoles().some(has(ROLE_B2BCONSUMERDISTRIBUTOR));
};
